import { supabase } from '../lib/supabase';

export const paymentsService = {
  async createCheckoutSession({ formId, submissionData, amount, protection = {}, experimentContext = null, files = {}, idempotencyKey }) {
    try {
      // Always use multipart: it allows paid forms with file fields to hand the
      // actual files to the checkout function before leaving this origin.
      const body = new FormData();
      body.append('payload', JSON.stringify({
        purpose: 'form_payment',
        formId,
        submissionData,
        idempotencyKey,
        currentRoute: window.location.pathname,
        source: 'web',
        userAgent: navigator.userAgent || null,
        ...(amount != null ? { amount } : {}),
        turnstileToken: protection?.turnstileToken || null,
        honeypot: protection?.honeypot || '',
        ...(experimentContext?.testId && experimentContext?.variantId ? {
          abTestId: experimentContext.testId,
          abVariantId: experimentContext.variantId,
          abVisitorId: experimentContext.visitorId,
        } : {}),
      }));
      Object.entries(files).forEach(([fieldId, fieldFiles]) => {
        fieldFiles.forEach((file) => body.append(`file:${fieldId}`, file, file.name));
      });
      const { data, error } = await supabase.functions.invoke('create-checkout-session', { body });

      if (error) {
        // FunctionsHttpError carries the raw Response in error.context.
        // Parse the JSON body so callers receive the server's friendly message.
        if (error?.context?.json) {
          try {
            const body = await error.context.json();
            const friendly = body?.details || body?.error || error.message;
            const enriched = new Error(friendly);
            enriched.details = body?.details;
            enriched.serverError = body?.error;
            enriched.fieldErrors = body?.fieldErrors;
            throw enriched;
          } catch (parseErr) {
            // If the body can't be parsed just re-throw the original.
            if (parseErr !== error) throw parseErr;
          }
        }
        throw error;
      }
      if (data?.blocked === true) return data;
      if (!data?.url || !data?.sessionId) throw new Error('Checkout service did not return a checkout URL.');
      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  async createPlanSignupCheckout({ plan, billingCycle, account, idempotencyKey }) {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          purpose: 'plan_signup',
          planKey: plan,
          billingCycle,
          email: account.email,
          name: account.fullName,
          idempotencyKey,
          currentRoute: `${window.location.pathname}?plan=${encodeURIComponent(plan)}&billing=${encodeURIComponent(billingCycle)}`,
        },
      });
      if (error) throw error;
      if (!data?.url || !data?.sessionId) throw new Error('Checkout service did not return a checkout URL.');
      return data;
    } catch (error) {
      console.error('Error creating plan signup checkout:', error);
      throw error;
    }
  },

  async getCheckoutSessionStatus(sessionId, purpose) {
    if (!sessionId) throw new Error('Missing checkout session ID.');
    const { data, error } = await supabase.functions.invoke('checkout-session-status', {
      body: { sessionId, purpose },
    });
    if (error) throw error;
    return data;
  },

  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  },

  async getPaymentTransactions(formId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify user owns the form
      const { data: form } = await supabase?.from('forms')?.select('id')?.eq('id', formId)?.eq('user_id', user?.id)?.single();

      if (!form) throw new Error('Form not found or access denied');

      const { data, error } = await supabase?.from('payment_transactions')?.select('*')?.eq('form_id', formId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching payment transactions:', error);
      throw error;
    }
  }
};